/**
 * Bundle offer on the store's Bundle offers (THEME.md §7.9 **Bundle offer**, §7.15 **Complete the bundle**; D-128,
 * D-142, D-144; T-190, T-238, T-284): one element, `okt-bundle-offer`, for the product page and the cart drawer,
 * bringing the child blocks of blocks/okt-bundle-offer.liquid to life.
 *
 * - Data: Liquid draws the add-ons (photo, title, the price less the offer's Percent) and the Compact line with the
 *   page. The parent's `data-okt-bundle` holds the offer (the Bundle details entry's handle, its Percent and whether
 *   the discount applies to the whole set), the items (up to 3 products, each with its variants: id, availability,
 *   option values and price in minor units) and this product; `data-okt-context` the currency, money format and
 *   strings. Nothing is fetched to draw the block.
 * - The discount is Function Studio's: its rule for the offer discounts the lines tagged `_okt_bundle` with the
 *   entry's handle, a hidden line property: the add-ons, and in Whole set this product's line too. The block shows the
 *   offer's price; after an add, each add-on shows the price the cart gave it, so the block never claims a saving the
 *   cart doesn't show (OP5 a).
 * - Product page: the product form's Add to cart adds the add-ons the shopper ticked (assets/product-form.js, §9.4
 *   #42, `okt:product-form-add`), in one request: the add-ons first, each tagged and nested under the main variant
 *   (`parent_id`), then the main line, tagged too in Whole set; with nothing ticked the form's own request, untagged.
 *   A pre-ticked add-on (Selected at start On) counts only with the block's own Add to cart and never rides along on
 *   the product's button (EU Consumer Rights Directive, Art. 22); without an Add to cart block every add-on starts
 *   unticked.
 * - Cart drawer: each add-on's Add adds it tagged under the cart line (`parent_line_key`) in one request; in Whole set
 *   the line is tagged first (one cart change, unless it has the tag). Horizon's drawer re-renders from the reply.
 * - Quick view (D-142 BU5): Horizon's quick add window shows the add-on; its button becomes "Choose for the bundle"
 *   (ticks the add-on in that size) or, in the drawer, "Add" (adds it tagged under the line). No Horizon file changes:
 *   the window's button is caught on the window's capture phase, before Horizon's delegated handlers.
 * - Compact (D-142 BU11): on a product page one summary line opens the full layout in place; in the drawer each
 *   add-on is one line whose button reads Choose, opens its sizes, then reads "Add M".
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/** The line tag Function Studio's bundle rule reads: the Bundle details entry's handle (D-144, 29 Oct) */
const TAG = '_okt_bundle';
const SIZE_NAME = 'okt-bundle-size';

/** @param {string|number} gid */
const numericId = (gid) => String(gid).split('/').pop() || '';

/** Horizon's routes (a script-level `Theme` constant, not on window) */
function routes() {
  // @ts-ignore
  return (typeof Theme !== 'undefined' && Theme.routes) || {};
}

/** The cart sections Horizon re-renders after an add */
function cartSections() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
    .filter(Boolean);
}

/** Horizon's strike-through for sold-out values (snippets/strikethrough-variant.liquid) */
const STRIKE =
  '<svg viewBox="0 0 100 46" preserveAspectRatio="xMidYMid slice" class="variant-option__strikethrough" aria-hidden="true">' +
  '<line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke" />' +
  '<line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke" /></svg>';

let quickViewDefined = /** @type {Promise<void> | null} */ (null);

/**
 * Defines `okt-bundle-quick-view`: Horizon's quick add component for a product URL of our choosing
 * @returns {Promise<void>}
 */
function defineQuickView() {
  quickViewDefined ??= import('@theme/quick-add').then(({ QuickAddComponent }) => {
    if (customElements.get('okt-bundle-quick-view')) return;
    class OktBundleQuickView extends QuickAddComponent {
      get productPageUrl() {
        return this.dataset.productUrl || '';
      }
    }
    customElements.define('okt-bundle-quick-view', OktBundleQuickView);
  });
  return quickViewDefined;
}

/**
 * One add-on of the offer and its Item block. Its data: `product` (id), `title`, `url`, `available`, `options` (the
 * option names) and `variants` (`id`, `available`, `options`: the values in the options' order, `price` in minor units)
 */
class BundleItem {
  /** @param {OktBundleOffer} bundle @param {HTMLElement} element @param {any} data @param {number} index */
  constructor(bundle, element, data, index) {
    this.bundle = bundle;
    this.element = element;
    this.body = /** @type {HTMLElement} */ (element.querySelector('.okt-bundle-item__body') || element);
    this.data = data;
    this.index = index;
    /** @type {any[]} */ this.variants = [];
    /** @type {string[]} Option names that vary across the add-on's variants */
    this.varying = [];
    this.available = false;
    this.checked = false;
    /** The shopper ticked it themselves */
    this.chosen = false;
    /** The shopper picked its size themselves */
    this.picked = false;
    /** Drawer, Compact: its sizes are open under its line */
    this.open = false;
  }

  /** @param {string} selector */
  part(selector) {
    return /** @type {HTMLElement | null} */ (this.body.querySelector(selector));
  }

  get select() {
    return /** @type {HTMLInputElement | null} */ (this.part('.okt-bundle-select__input'));
  }

  get selectStyle() {
    return this.part('.okt-bundle-select')?.dataset.style || '';
  }

  /** Always in the set: Select None, or no Select block, where the block has its own Add to cart */
  get included() {
    return this.bundle.own && (!this.select || this.selectStyle === 'none');
  }

  get sizes() {
    return this.part('.okt-bundle-sizes');
  }

  /** The add-on's page, as Liquid gives it (in the shopper's language) */
  get url() {
    return String(this.data?.url || '');
  }

  /** @param {any} variant @param {string} name */
  value(variant, name) {
    const index = (this.data.options || []).indexOf(name);
    return index >= 0 ? String(variant.options?.[index] ?? '') : '';
  }

  /** The size's label: the values of the options that vary @param {any} variant */
  label(variant) {
    return this.varying.map((name) => this.value(variant, name)).join(' / ');
  }

  /** @param {any} variant @returns {number} In minor units */
  regular(variant) {
    return Number(variant.price) || 0;
  }

  /** @param {number} regular */
  discounted(regular) {
    return this.bundle.discounted(regular);
  }

  /** The chosen size, or the only variant; null while a size is to be chosen */
  selectedVariant() {
    if (!this.varying.length) return this.variants.find((variant) => variant.available) ?? null;
    const id = this.sizeValue();
    return id ? this.variants.find((variant) => String(variant.id) === id) ?? null : null;
  }

  /** The variant the price shows: the chosen one, else the size matching the product's, else the first available */
  shownVariant() {
    return this.selectedVariant() || this.match() || this.variants.find((variant) => variant.available) || null;
  }

  /** The available variant matching the main product's chosen values */
  match() {
    if (!this.varying.length) return null;
    const values = (this.bundle.main?.options || []).map((/** @type {string} */ value) => String(value).toLowerCase());
    return (
      this.variants.find(
        (variant) =>
          variant.available && this.varying.every((name) => values.includes(this.value(variant, name).toLowerCase()))
      ) ?? null
    );
  }

  /** @returns {HTMLInputElement[]} */
  sizeInputs() {
    return Array.from(this.sizes?.querySelectorAll('input[type="radio"]') ?? []);
  }

  get sizeSelect() {
    return /** @type {HTMLSelectElement | null} */ (this.sizes?.querySelector('select') ?? null);
  }

  /** The chosen variant's numeric id, or '' */
  sizeValue() {
    const select = this.sizeSelect;
    if (select) return select.value;
    return this.sizeInputs().find((input) => input.checked)?.value || '';
  }

  /** @param {string} id */
  setSize(id) {
    const select = this.sizeSelect;
    if (select) select.value = id;
    for (const input of this.sizeInputs()) input.checked = input.value === id;
  }

  /** How many go in: the quantity selector's number where it shows, else 1 */
  quantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.part('.okt-bundle-quantity input'));
    if (!input || this.part('.okt-bundle-quantity')?.hidden) return 1;
    return Math.max(1, parseInt(input.value, 10) || 1);
  }

  /** Sets the item up from its data (Liquid drew its photo, title and price): sizes, names, controls */
  setup() {
    const variants = Array.isArray(this.data.variants) ? this.data.variants : [];
    this.available = this.data.available !== false && variants.some((/** @type {any} */ variant) => variant.available);
    this.element.hidden = !this.available;
    if (!this.available) return;

    this.variants = variants;
    this.varying = (this.data.options || []).filter(
      (/** @type {string} */ name) => new Set(variants.map((variant) => this.value(variant, name))).size > 1
    );

    const ids = `${this.bundle.id}-${this.index}`;
    const name = String(this.data.title || '');
    const title = this.part('.okt-bundle-title__text');
    if (title) title.id = `${ids}-title`;
    this.#label();
    this.#fillSizes();
    const quantity = this.part('.okt-bundle-quantity');
    if (quantity) {
      if (title) quantity.setAttribute('aria-labelledby', title.id);
      else quantity.setAttribute('aria-label', name);
    }
    const select = this.select;
    if (select) {
      if (title) select.setAttribute('aria-labelledby', title.id);
      else select.setAttribute('aria-label', name);
      select.disabled = false;
    }
    const add = /** @type {HTMLButtonElement | null} */ (this.part('.okt-bundle-add__button'));
    if (add) {
      add.disabled = false;
      if (title) add.setAttribute('aria-describedby', title.id);
    }
    this.body.dataset.state = 'ready';
  }

  /** Title, photo and View link: the photo takes the focus itself only where no title or View link opens anything */
  #label() {
    const frame = this.part('.okt-bundle-image__frame[data-okt-open]');
    if (frame && !this.part('.okt-bundle-title [data-okt-open], .okt-bundle-link [data-okt-open]')) {
      frame.removeAttribute('tabindex');
      frame.removeAttribute('aria-hidden');
      frame.setAttribute('aria-label', String(this.data.title || ''));
    }
    const link = this.part('.okt-bundle-link__action');
    const title = this.part('.okt-bundle-title__text');
    if (link && title) link.setAttribute('aria-describedby', title.id);
  }

  /** Builds Horizon's size buttons (a radio group) or its dropdown */
  #fillSizes() {
    const sizes = this.sizes;
    if (!sizes) return;
    if (!this.varying.length) {
      sizes.hidden = true;
      return;
    }
    const name = `${SIZE_NAME}-${this.bundle.id}-${this.index}`;
    const form = this.bundle.mode === 'product' ? this.bundle.main?.form || '' : '';
    const legend = /** @type {HTMLElement | null} */ (sizes.querySelector('.okt-bundle-sizes__legend'));
    if (legend) legend.textContent = this.varying.join(' / ');
    const select = this.sizeSelect;

    if (select) {
      select.id = `${name}-select`;
      select.name = name;
      if (legend instanceof HTMLLabelElement) legend.htmlFor = select.id;
      if (form) select.setAttribute('form', form);
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = this.bundle.context.t.chooseSize;
      placeholder.disabled = true;
      const options = [placeholder];
      for (const variant of this.variants) {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = variant.available ? this.label(variant) : `${this.label(variant)} - ${this.bundle.context.t.soldOut}`;
        option.disabled = !variant.available;
        options.push(option);
      }
      select.replaceChildren(...options);
      select.value = '';
      select.disabled = false;
    } else {
      const fieldset = /** @type {HTMLFieldSetElement | null} */ (sizes.querySelector('fieldset'));
      if (!fieldset) return;
      fieldset.querySelectorAll('label').forEach((label) => label.remove());
      for (const variant of this.variants) {
        const text = this.label(variant);
        const label = document.createElement('label');
        label.className = 'variant-option__button-label';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = name;
        input.value = String(variant.id);
        input.dataset.optionAvailable = String(!!variant.available);
        input.setAttribute('aria-label', variant.available ? text : `${text} - ${this.bundle.context.t.soldOut}`);
        if (form) input.setAttribute('form', form);
        if (!variant.available) input.disabled = true;
        label.append(input);
        if (variant.available) {
          const pill = document.createElement('span');
          pill.className = 'variant-option__button-label__pill';
          label.append(pill);
        }
        const span = document.createElement('span');
        span.className = 'variant-option__button-label__text';
        span.textContent = text;
        label.append(span);
        if (!variant.available) label.insertAdjacentHTML('beforeend', STRIKE);
        fieldset.append(label);
      }
      fieldset.disabled = false;
    }
    sizes.id = `${name}-sizes`;
    this.preselect();
  }

  /** Selects the add-on's size matching the main product's, until the shopper picks one */
  preselect() {
    if (this.picked || !this.varying.length) return;
    const match = this.match();
    this.setSize(match ? String(match.id) : '');
  }

  /** Whether the sizes show now */
  sizesShown() {
    const sizes = this.sizes;
    if (!sizes || !this.varying.length) return false;
    if (this.bundle.mode === 'cart' && this.bundle.compact) return this.open;
    // Always, or When selected (the default, also for a value saved before T-284)
    if (sizes.dataset.show === 'always') return true;
    const selectable = !!this.select && this.selectStyle !== 'none';
    return selectable ? this.checked : true;
  }

  /** Shows the item's state: tick, sizes, quantity, price, the drawer's button */
  update() {
    if (!this.available) return;
    const select = this.select;
    if (select) select.checked = this.checked;
    if (this.checked) this.body.dataset.selected = '';
    else delete this.body.dataset.selected;

    const sizes = this.sizes;
    if (sizes) {
      const shown = this.sizesShown();
      sizes.hidden = !shown;
      const control = /** @type {HTMLFieldSetElement | HTMLSelectElement | null} */ (sizes.querySelector('fieldset, select'));
      if (control) control.disabled = !shown;
    }
    // The Quantity block renders only on a product page and with Show When selected
    const quantity = this.part('.okt-bundle-quantity');
    if (quantity) quantity.hidden = !(this.checked && this.bundle.mode === 'product');

    const variant = this.shownVariant();
    if (variant) {
      const regular = this.regular(variant);
      this.showPrice(this.discounted(regular), regular);
    }
    this.#updateAdd();
  }

  /**
   * Shows a price: the price after the discount, the price before it struck, the saving
   * @param {number} now - In minor units
   * @param {number} regular - In minor units
   */
  showPrice(now, regular) {
    const price = this.part('.okt-bundle-price');
    if (!price) return;
    const t = this.bundle.context.t;
    const discounted = now < regular;
    const nowElement = price.querySelector('[data-okt-now]');
    const wasElement = price.querySelector('[data-okt-was]');
    const savingElement = price.querySelector('[data-okt-saving]');
    if (nowElement) nowElement.textContent = this.bundle.money(now);
    const was = /** @type {HTMLElement | null} */ (wasElement?.closest('.compare-at-price') ?? null);
    if (was && wasElement) {
      was.hidden = !discounted;
      wasElement.textContent = discounted ? this.bundle.money(regular) : '';
    }
    const badges = /** @type {HTMLElement | null} */ (savingElement?.closest('.okt-price-badges') ?? null);
    if (badges && savingElement) {
      badges.hidden = !discounted;
      const percent = discounted ? Math.max(1, Math.round(((regular - now) * 100) / regular)) : 0;
      savingElement.textContent = !discounted
        ? ''
        : price.dataset.savings === 'amount'
        ? t.amount.replace('[amount]', this.bundle.money(regular - now))
        : t.percent.replace('[percent]', String(percent));
    }
  }

  /** The drawer's button: Choose, then "Add M"; elsewhere its label */
  #updateAdd() {
    const button = /** @type {HTMLButtonElement | null} */ (this.part('.okt-bundle-add__button'));
    if (!button) return;
    const label = /** @type {HTMLElement | null} */ (button.querySelector('.okt-bundle-add__label'));
    const caret = /** @type {HTMLElement | null} */ (button.querySelector('.okt-bundle-add__caret'));
    const t = this.bundle.context.t;
    const own = button.dataset.label || '';
    const compactSizes = this.bundle.mode === 'cart' && this.bundle.compact && this.varying.length > 0;
    let text = own || t.add;
    // Choose opens the sizes under the line (and closes them again until one is picked); then "Add M"
    const variant = compactSizes && this.open ? this.selectedVariant() : null;
    if (compactSizes && !variant) {
      text = t.choose;
      button.setAttribute('aria-expanded', String(this.open));
      if (this.sizes) button.setAttribute('aria-controls', this.sizes.id);
    } else {
      if (variant) text = t.addSize.replace('[size]', this.label(variant));
      button.removeAttribute('aria-expanded');
      button.removeAttribute('aria-controls');
    }
    if (label) label.textContent = text;
    if (caret) caret.hidden = !(compactSizes && !variant);
  }

  /** Ticked without a size where it must have one */
  missingSize() {
    return this.varying.length > 0 && !this.selectedVariant();
  }

  /** Shows a message under the item's Add, or nowhere @param {string} text */
  message(text) {
    const message = this.part('.okt-bundle-add__message');
    if (message) message.textContent = text;
  }

  /** Focuses the first size to choose */
  focusSizes() {
    const select = this.sizeSelect;
    if (select) return select.focus();
    this.sizeInputs()
      .find((input) => !input.disabled)
      ?.focus();
  }
}

/**
 * The Bundle offer
 */
class OktBundleOffer extends HTMLElement {
  /** @type {any} */ data = null;
  /** @type {any} */ context = null;
  /** @type {BundleItem[]} */ items = [];
  mode = 'product';
  compact = false;
  /** The block has its own Add to cart */
  own = false;
  /** Compact: the panel is open */
  open = false;
  ready = false;
  #identity = '';
  /** The shared add whose reply we're waiting for */
  #pending = false;
  #abortController = new AbortController();
  #observer = new MutationObserver((records) => this.#onMutation(records));
  #filling = false;
  /** The last render hid the block (no add-on to offer) */
  #hidden = false;
  /** @type {HTMLElement | null} */ #quickView = null;

  get main() {
    return this.data?.main ?? null;
  }

  /** The offer's Percent (1 to 90) */
  get pct() {
    return Number(this.data?.offer?.pct || 0);
  }

  /** Discount applies to Whole set: this product's line is tagged and priced with the set */
  get set() {
    return !!this.data?.offer?.set;
  }

  /**
   * The price less the offer's Percent, rounded up to the cent, as Liquid draws it (Kaching rounded so; Function
   * Studio's rounding is one of the switch's checks, T-285). In the drawer, once the cart shows the offer's discount
   * isn't running (`claim` false: a tagged line without a discount), the price as it is (OP5 a).
   * @param {number} regular - In minor units
   */
  discounted(regular) {
    if (this.data?.offer?.claim === false) return regular;
    return Math.ceil((regular * (100 - this.pct)) / 100);
  }

  get section() {
    return this.closest('.shopify-section');
  }

  get summary() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('.okt-bundle__summary'));
  }

  get panel() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.okt-bundle__panel'));
  }

  get ownButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('.okt-bundle-button__button'));
  }

  /** @param {number} cents */
  money(cents) {
    return formatMoney(cents, this.context.format, this.context.currency);
  }

  connectedCallback() {
    // Quick add (Horizon's window) shows the product page's details: the offer stays out there (the cart drawer is a
    // dialog too, so only the quick add window counts)
    if (this.closest('quick-add-dialog, .quick-add-modal')) {
      this.hidden = true;
      return;
    }
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('invalid', this.#onInvalid, { signal, capture: true });
    // The add-ons' quantity stays ours: Horizon's sticky add to cart and volume prices read every quantity selector's
    // update on the document as the product's
    this.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      (event) => {
        event.stopPropagation();
        this.#refreshTotals();
      },
      { signal }
    );

    this.#readData();
    if (this.mode === 'product') {
      const section = this.section;
      section?.addEventListener('okt:product-form-add', this.#onFormAdd, { signal });
      document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
      document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
      document.addEventListener(ThemeEvents.quantitySelectorUpdate, () => this.#refreshTotals(), { signal });
    } else {
      // A drawer refresh morphs new markup in: a new line or offer comes in the attributes, new items as children,
      // and parts it drew anew are loading again
      this.#observer.observe(this, {
        attributes: true,
        attributeFilter: ['data-okt-bundle', 'data-state', 'hidden'],
        childList: true,
        subtree: true,
      });
    }
    this.#render();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#observer.disconnect();
  }

  #readData() {
    try {
      this.data = JSON.parse(this.dataset.oktBundle || 'null');
      this.context = JSON.parse(this.dataset.oktContext || 'null');
    } catch {
      this.data = null;
    }
    // Liquid's `t` filter escapes the strings for HTML ("Bundle &amp; save"); they go in as text
    if (this.context?.t) {
      const decoder = document.createElement('textarea');
      const decode = (/** @type {string} */ text) => {
        decoder.innerHTML = text;
        return decoder.value;
      };
      for (const [key, value] of Object.entries(this.context.t)) {
        this.context.t[key] = Array.isArray(value) ? value.map(decode) : decode(String(value));
      }
    }
    this.mode = this.data?.mode === 'cart' ? 'cart' : 'product';
    this.compact = this.classList.contains('okt-bundle--compact');
  }

  /** Reads the data and sets every part up; hides the block when no add-on can be offered */
  #render() {
    this.#readData();
    this.#identity = this.dataset.oktBundle || '';
    if (!this.data?.items?.length || !this.context) {
      this.hidden = this.#hidden = true;
      this.#observer.takeRecords();
      return;
    }
    this.#filling = true;
    this.own = !!this.ownButton;

    // The items drawn: one Item block per add-on, by its product id; one the drawer's refresh kept keeps its state
    const byId = new Map(this.data.items.map((/** @type {any} */ item, /** @type {number} */ index) => [String(item.product), index]));
    const previous = new Map(this.items.map((item) => [item.element, item]));
    this.items = [];
    for (const element of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-okt-bundle-item]'))) {
      const index = byId.get(element.dataset.oktBundleItem || '');
      if (index === undefined) {
        element.hidden = true;
        continue;
      }
      const data = this.data.items[index];
      const kept = previous.get(element);
      const same = !!kept && JSON.stringify(kept.data) === JSON.stringify(data);
      const item = same && kept ? kept : new BundleItem(this, element, data, index);
      item.index = index;
      if (!same) item.setup();
      this.items.push(item);
    }
    const shown = this.items.filter((item) => item.available);
    this.hidden = this.#hidden = shown.length === 0;
    if (this.hidden) {
      this.#observer.takeRecords();
      this.#filling = false;
      return;
    }

    // Selected at start: only with the block's own Add to cart (Art. 22); None (or no Select) always in the set
    for (const item of this.items) {
      if (previous.has(item.element) && previous.get(item.element) === item) continue;
      const preselected = item.part('.okt-bundle-select')?.dataset.start === 'on';
      item.checked = item.included || (this.own && !!item.select && preselected);
      item.chosen = false;
      // None without an Add to cart block: the add-on shows a checkbox instead
      const select = item.part('.okt-bundle-select');
      if (select && item.selectStyle === 'none') select.hidden = this.own;
    }

    this.#fillPhotos();
    this.#labelGroup();
    for (const item of this.items) item.update();
    this.#refreshTotals();
    this.ready = true;
    if (this.mode === 'cart' && this.ownButton) this.ownButton.disabled = false;
    else if (this.ownButton) this.ownButton.disabled = !!this.data.sample;
    this.#observer.takeRecords();
    this.#filling = false;
  }

  /**
   * The Photo strip's add-ons and the summary's photos (Liquid draws them) follow the items shown; the theme editor's
   * sample item lends the summary its Image block's photo
   */
  #fillPhotos() {
    for (const part of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.okt-bundle-photos__part'))) {
      const index = Number(part.dataset.oktBundlePhoto);
      const item = this.items.find((entry) => entry.index === index);
      part.hidden = !item?.available;
    }

    const photos = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.okt-bundle__summary-photo'));
    const shown = this.items.filter((item) => item.available);
    photos.forEach((photo, position) => {
      const item = shown[position];
      photo.hidden = !item;
      const source = /** @type {HTMLImageElement | null} */ (item?.body.querySelector('.okt-bundle-image__frame img') ?? null);
      if (source && !photo.querySelector('img')) {
        const image = document.createElement('img');
        image.src = source.currentSrc || source.src;
        image.alt = '';
        image.decoding = 'async';
        photo.replaceChildren(image);
      }
    });
  }

  /** The group's name: its Heading; the summary's line */
  #labelGroup() {
    const heading = /** @type {HTMLElement | null} */ (this.querySelector('.okt-bundle-heading'));
    if (heading) {
      heading.id ||= `${this.id}-heading`;
      this.setAttribute('aria-labelledby', heading.id);
    } else {
      this.setAttribute('aria-label', this.context.t.heading);
    }
  }

  /**
   * Whole set: this product is priced with the set once an add-on is in it, on a product page; in the drawer its line
   * is in the cart already, so it keeps the price the cart gives it (OP5 a)
   * @param {BundleItem[]} ticked
   */
  #mainInSet(ticked) {
    return this.set && this.mode === 'product' && ticked.length > 0;
  }

  /** Totals, the own button's label, this product's price, the summary line */
  #refreshTotals() {
    if (!this.ready && !this.#filling) return;
    const t = this.context.t;
    const shown = this.items.filter((item) => item.available);
    const ticked = shown.filter((item) => item.checked);

    // Total (D-142 BU7): this product × its quantity (in Whole set less the Percent), plus each ticked add-on less it
    const mainQuantity = this.#mainQuantity();
    const unit = Number(this.main?.price || 0);
    const inSet = this.#mainInSet(ticked);
    let now = (inSet ? this.discounted(unit) : unit) * mainQuantity;
    let was = unit * mainQuantity;
    this.#refreshMainPrice(inSet);
    for (const item of ticked) {
      const variant = item.shownVariant();
      if (!variant) continue;
      const regular = item.regular(variant);
      now += item.discounted(regular) * item.quantity();
      was += regular * item.quantity();
    }
    for (const total of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.okt-bundle-total'))) {
      const nowElement = total.querySelector('[data-okt-total]');
      const wasElement = total.querySelector('[data-okt-total-was]');
      const saving = /** @type {HTMLElement | null} */ (total.querySelector('[data-okt-total-saving]'));
      if (nowElement) nowElement.textContent = this.money(now);
      const strike = /** @type {HTMLElement | null} */ (wasElement?.closest('s') ?? null);
      if (strike && wasElement) {
        strike.hidden = !(was > now);
        wasElement.textContent = this.money(was);
      }
      if (saving) {
        const mode = total.dataset.savings;
        saving.hidden = !(was > now) || mode === 'off';
        const percent = was > 0 ? Math.max(1, Math.round(((was - now) * 100) / was)) : 0;
        saving.textContent = t.youSave.replace('[amount]', mode === 'percent' ? `${percent}%` : this.money(was - now));
      }
      total.dataset.state = 'ready';
    }

    // The own Add to cart: "Add all 3 to cart" with the count, "Add to cart" with nothing ticked
    const own = this.ownButton;
    if (own) {
      const label = own.querySelector('.okt-bundle-button__label');
      const count = 1 + ticked.length;
      if (label && !own.dataset.label) label.textContent = count > 1 ? t.addAll.replace('[count]', String(count)) : t.addToCart;
    }
    this.#refreshSummary();
    this.#validate();
  }

  /**
   * This product's price ("This item · Size M · €42.42 €49.90"): in Whole set, once an add-on is in the set, less the
   * Percent with the price before struck
   * @param {boolean} inSet
   */
  #refreshMainPrice(inSet) {
    const price = this.querySelector('[data-okt-main-price]');
    if (!price || !this.main) return;
    const unit = Number(this.main.price || 0);
    const now = inSet ? this.discounted(unit) : unit;
    price.textContent = this.money(now);
    const was = /** @type {HTMLElement | null} */ (this.querySelector('[data-okt-main-was]'));
    if (was) {
      was.hidden = !(now < unit);
      was.textContent = now < unit ? this.money(unit) : '';
    }
  }

  /** The product form's quantity on a product page; the line's in the drawer */
  #mainQuantity() {
    if (this.mode === 'cart') return Number(this.main?.quantity || 1);
    const form = this.main?.form ? document.getElementById(this.main.form) : null;
    const input = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]') ?? null);
    return Math.max(1, parseInt(input?.value || '1', 10) || 1);
  }

  /** Compact: "Bundle & save · 2 add-ons" over "Save up to €6.99", "1 of 2 added · You save €3.69" or the set's saving */
  #refreshSummary() {
    const summary = this.summary;
    if (!summary) return;
    const t = this.context.t;
    const shown = this.items.filter((item) => item.available);
    const ticked = shown.filter((item) => item.checked);
    const count = shown.length;
    const heading = this.querySelector('.okt-bundle-heading')?.textContent?.trim() || t.heading;
    const title = /** @type {HTMLElement} */ (summary.querySelector('.okt-bundle__summary-title'));
    const status = /** @type {HTMLElement} */ (summary.querySelector('.okt-bundle__summary-status'));
    title.textContent = `${heading} · ${t.counts[Math.min(count, 3) - 1] || ''}`;

    // The add-ons' saving, and in Whole set this product's (× its quantity once the set is ticked)
    /** @param {BundleItem[]} items */
    const saving = (items) => {
      let sum = items.reduce((total, item) => {
        const variant = item.shownVariant();
        if (!variant) return total;
        const regular = item.regular(variant);
        return total + (regular - item.discounted(regular)) * (item.checked ? item.quantity() : 1);
      }, 0);
      if (this.set && this.mode === 'product' && items.length) {
        const unit = Number(this.main?.price || 0);
        sum += (unit - this.discounted(unit)) * (items.some((item) => item.checked) ? this.#mainQuantity() : 1);
      }
      return sum;
    };
    const amount = (/** @type {string} */ template, /** @type {number} */ cents) => {
      const span = document.createElement('span');
      span.dataset.oktSaving = '';
      span.textContent = template.replace('[amount]', this.money(cents));
      return span;
    };
    if (ticked.length && (this.open || ticked.some((item) => item.chosen))) {
      const added = document.createElement('span');
      added.dataset.oktAdded = '';
      added.textContent = `${t.added.replace('[added]', String(ticked.length)).replace('[count]', String(count))} · `;
      status.replaceChildren(added, amount(t.youSave, saving(ticked)));
    } else if (ticked.length) {
      status.replaceChildren(amount(t.saveSet, saving(ticked)));
    } else {
      status.replaceChildren(amount(t.saveUpTo, saving(shown)));
    }
    summary.dataset.state = 'ready';
  }

  /** Opens or closes the compact panel @param {boolean} open */
  #toggle(open) {
    const summary = this.summary;
    const panel = this.panel;
    if (!summary || !panel) return;
    this.open = open;
    summary.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    this.#refreshSummary();
  }

  /**
   * The add-ons that ride on the product form's own Add to cart: ticked by the shopper themselves (a pre-ticked
   * add-on counts only with the block's own button, Art. 22)
   */
  #riding() {
    return this.items.filter((item) => item.available && item.checked && item.chosen);
  }

  /** Ticked without a size: the first size carries the message, so the product form is invalid */
  #validate() {
    if (this.mode !== 'product') return;
    const riding = new Set(this.#riding());
    for (const item of this.items) {
      const missing = riding.has(item) && item.missingSize();
      const controls = item.sizeSelect ? [item.sizeSelect] : item.sizeInputs();
      for (const control of controls) control.setCustomValidity('');
      const first = controls.find((control) => !control.disabled);
      if (missing && first) first.setCustomValidity(this.context.t.chooseSize);
    }
  }

  /** The browser points at a size to choose: open the panel first, so it can show it @param {Event} event */
  #onInvalid = (event) => {
    if (this.compact && this.mode === 'product' && !this.open && this.contains(/** @type {Node} */ (event.target))) {
      this.#toggle(true);
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    const element = /** @type {HTMLElement} */ (target).closest?.('[data-okt-bundle-item]');
    const item = this.items.find((entry) => entry.element === element);
    if (!item) return;
    if (target instanceof HTMLInputElement && target.classList.contains('okt-bundle-select__input')) {
      item.checked = target.checked;
      item.chosen = target.checked;
      if (!target.checked) item.message('');
    } else if (
      (target instanceof HTMLInputElement && target.type === 'radio') ||
      target instanceof HTMLSelectElement
    ) {
      item.picked = true;
      item.message('');
      this.#message('');
    } else {
      return;
    }
    item.update();
    this.#refreshTotals();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!(target instanceof Element)) return;

    if (target.closest('.okt-bundle__summary')) {
      this.#toggle(!this.open);
      return;
    }
    const element = target.closest('[data-okt-bundle-item]');
    const item = this.items.find((entry) => entry.element === element);
    const photo = target.closest('.okt-bundle-photos__part');

    const opener = /** @type {HTMLElement | null} */ (target.closest('[data-okt-open]'));
    if (opener && (item || photo)) {
      const owner = item || this.items.find((entry) => entry.index === Number(/** @type {HTMLElement} */ (photo).dataset.oktBundlePhoto));
      if (!owner?.available) return;
      if (opener.dataset.oktOpen === 'quick') {
        event.preventDefault();
        this.#openQuickView(owner, opener);
      }
      return;
    }
    if (target.closest('.okt-bundle-add__button') && item) {
      this.#onItemAdd(item);
      return;
    }
    if (target.closest('.okt-bundle-button__button')) {
      this.#onOwnAdd();
      return;
    }
    // A tap anywhere else on a checkbox or toggle item ticks it, as on today's card
    if (item && item.select && ['checkbox', 'toggle'].includes(item.selectStyle) && !item.select.disabled) {
      if (target.closest('a, button, input, select, label, textarea, .okt-bundle-sizes, .okt-bundle-quantity')) return;
      item.select.click();
    }
  };

  /** A message under the own button @param {string} text */
  #message(text) {
    const message = this.querySelector('.okt-bundle-button__message');
    if (message) message.textContent = text;
  }

  /** The main product's size: the add-ons follow it until the shopper picks theirs @param {Event} event */
  #onProductSelect = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!(target instanceof Element) || !this.section?.contains(target) || target.closest('product-card, quick-add-dialog')) return;
    const select = /** @type {any} */ (event);
    // The event names the product by its GID
    if (select.product?.id && numericId(select.product.id) !== String(this.main?.product)) return;
    if (Array.isArray(select.selectedOptions) && this.main) {
      // The picker reports the options it shows; the others keep the variant's values
      const names = Array.isArray(this.main.names) ? this.main.names : [];
      const values = [...(this.main.options || [])];
      for (const option of select.selectedOptions) {
        const index = names.indexOf(option.name);
        if (index >= 0) values[index] = option.value;
        else values.push(option.value);
      }
      this.main.options = values;
      this.#refreshMainOptions(select.selectedOptions);
      for (const item of this.items) {
        item.preselect();
        item.update();
      }
      this.#refreshTotals();
    }
    select.promise
      ?.then((/** @type {any} */ result) => {
        const resource = result?.detail?.resource;
        const cents = typeof resource?.price === 'number' ? resource.price : null;
        if (cents === null || !this.main) return;
        this.main.price = cents;
        this.main.variant = resource.id;
        // This product's price and the totals follow (#refreshMainPrice)
        this.#refreshTotals();
      })
      .catch(() => {});
  };

  /** This product's line: "· Size M" @param {{name: string, value: string}[]} options */
  #refreshMainOptions(options) {
    const element = this.querySelector('[data-okt-main-options]');
    if (!element || !Array.isArray(this.main?.varying)) return;
    const parts = options
      .filter((option) => this.main.varying.includes(option.name))
      .map((option) => `${option.name} ${option.value}`);
    element.textContent = parts.length ? ` · ${parts.join(', ')}` : '';
  }

  /** The product form's body without our size fields @param {FormData} formData */
  #formBody(formData) {
    const body = new FormData();
    for (const [key, value] of formData) if (!key.startsWith(SIZE_NAME)) body.append(key, value);
    return body;
  }

  /**
   * The bundle's shape, one request: the add-ons first, each tagged `_okt_bundle` and nested under the main variant,
   * then the main line with the form's quantity, selling plan and line properties, tagged too in Whole set
   * @param {FormData} body - The product form's body
   * @param {BundleItem[]} items
   */
  #bundleBody(body, items) {
    const mainId = String(body.get('id'));
    const handle = String(this.data.offer.handle);
    const lines = new FormData();
    for (const [key, value] of body) {
      if (!['id', 'quantity', 'selling_plan'].includes(key) && !key.startsWith('properties[')) lines.append(key, value);
    }
    items.forEach((item, position) => {
      const variant = /** @type {any} */ (item.selectedVariant());
      lines.append(`items[${position}][id]`, String(variant.id));
      lines.append(`items[${position}][quantity]`, String(item.quantity()));
      lines.append(`items[${position}][parent_id]`, mainId);
      lines.append(`items[${position}][properties][${TAG}]`, handle);
    });
    const main = items.length;
    lines.append(`items[${main}][id]`, mainId);
    lines.append(`items[${main}][quantity]`, String(body.get('quantity') || '1'));
    const plan = body.get('selling_plan');
    if (plan) lines.append(`items[${main}][selling_plan]`, plan);
    for (const [key, value] of body) {
      const property = key.match(/^properties\[(.+)\]$/);
      if (property && property[1] !== TAG) lines.append(`items[${main}][properties][${property[1]}]`, value);
    }
    if (this.set) lines.append(`items[${main}][properties][${TAG}]`, handle);
    return lines;
  }

  /**
   * Horizon's product form is about to send its add request (assets/product-form.js, §9.4 #42): with add-ons the
   * shopper ticked, the bundle's shape; without, the form's own request, untagged (our size fields left out).
   * @param {Event} event
   */
  #onFormAdd = (event) => {
    const source = /** @type {HTMLElement} */ (event.target);
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!this.ready || !this.data?.offer?.handle || this.data.sample || !detail?.formData || source.closest('quick-add-dialog')) return;
    const formData = /** @type {FormData} */ (detail.formData);
    if (!formData.get('id')) return;
    const body = this.#formBody(formData);
    const riding = this.#riding().filter((item) => item.selectedVariant());
    if (!riding.length) {
      detail.body = body;
      return;
    }
    detail.body = this.#bundleBody(body, riding);
    this.#pending = true;
  };

  /**
   * The cart right after the shared add: each add-on's real price, with the bundle's discount or without it
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    const promise = /** @type {any} */ (event).promise;
    if (!promise) return;
    // Horizon announces the add before it sends the request, so the add's lines are known only by the reply
    promise
      .then((/** @type {any} */ result) => {
        const detail = result?.detail;
        if (!this.#pending || detail?.source !== 'product-form-component' || !Array.isArray(detail.items)) return;
        this.#pending = false;
        this.#showCartPrices(detail.items);
      })
      .catch(() => {});
  };

  /**
   * Each add-on's price as the cart gave it (and this product's in Whole set): the block shows only what the cart
   * shows once the lines are in it (OP5 a); a tagged line without a discount also logs a warning
   * @param {any[]} lines
   */
  #showCartPrices(lines) {
    const handle = this.data?.offer?.handle;
    if (!handle) return;
    const tagged = lines.filter((/** @type {any} */ line) => line.properties?.[TAG] === handle);
    for (const item of this.items) {
      const line = tagged.find((/** @type {any} */ entry) => String(entry.product_id) === String(item.data.product));
      if (!line) continue;
      item.showPrice(line.final_price, line.original_price);
      if (!(line.final_price < line.original_price)) {
        console.warn('[okt-bundle-offer] No bundle discount on the line yet', handle, line.original_price, line.final_price);
      }
    }
    const main = tagged.find(
      (/** @type {any} */ line) => String(line.product_id) === String(this.main?.product) && !line.parent_relationship
    );
    const price = this.querySelector('[data-okt-main-price]');
    const was = /** @type {HTMLElement | null} */ (this.querySelector('[data-okt-main-was]'));
    if (this.set && main && price) {
      price.textContent = this.money(main.final_price);
      if (was) {
        was.hidden = !(main.final_price < main.original_price);
        was.textContent = main.final_price < main.original_price ? this.money(main.original_price) : '';
      }
    }
  }

  /** The block's own Add to cart */
  #onOwnAdd() {
    if (!this.ready || this.data.sample) return;
    const button = /** @type {HTMLButtonElement} */ (this.ownButton);
    const items = this.items.filter((item) => item.available && item.checked);
    const missing = items.find((item) => item.missingSize());
    if (missing) {
      this.#message(this.context.t.chooseSize);
      if (this.compact && !this.open) this.#toggle(true);
      missing.focusSizes();
      return;
    }
    this.#message('');
    if (this.mode === 'cart') this.#addToLine(items, button, (text) => this.#message(text));
    else this.#addWithProduct(items, button, (text) => this.#message(text));
  }

  /** An item's Add: in the drawer under its line; on a product page with the product @param {BundleItem} item */
  #onItemAdd(item) {
    if (!this.ready || this.data.sample) return;
    const button = /** @type {HTMLButtonElement} */ (item.part('.okt-bundle-add__button'));
    // Drawer, Compact: Choose opens the sizes under the line, or closes them while none is picked
    if (this.mode === 'cart' && this.compact && item.varying.length && (!item.open || !item.selectedVariant())) {
      item.open = !item.open;
      item.message('');
      item.update();
      return;
    }
    if (item.missingSize()) {
      item.message(this.context.t.chooseSize);
      item.focusSizes();
      return;
    }
    item.message('');
    if (this.mode === 'cart') this.#addToLine([item], button, (text) => item.message(text));
    else this.#addWithProduct([item], button, (text) => item.message(text));
  }

  /**
   * Adds the product (the product form's size, quantity and properties) with these add-ons, in one request
   * @param {BundleItem[]} items
   * @param {HTMLButtonElement} button
   * @param {(text: string) => void} message
   */
  async #addWithProduct(items, button, message) {
    const form = /** @type {HTMLFormElement | null} */ (this.main?.form ? document.getElementById(this.main.form) : null);
    if (!form) return;
    if (!form.reportValidity()) return;
    const add = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[name="add"]'));
    if (add?.disabled) {
      message(this.context.t.soldOut);
      return;
    }
    const body = this.#formBody(new FormData(form));
    const request = items.length ? this.#bundleBody(body, items) : body;
    const sections = cartSections();
    if (sections.length) {
      request.append('sections', sections.join(','));
      request.append('sections_url', window.location.pathname);
    }
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    message('');
    try {
      const response = await fetch(routes().cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: request,
      });
      const reply = await response.json();
      if (!response.ok || reply.status) throw new Error(reply.description || reply.message || 'add');
      const cart = await this.#announce('product', reply.sections, items, false);
      if (cart) this.#showCartPrices(cart.items || []);
    } catch (error) {
      message(this.context.t.error);
      console.warn('[okt-bundle-offer]', error);
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  /** The cart's line for this product: by its key, else by its variant, not nested @param {any[]} lines */
  #findMain(lines) {
    return (
      lines.find((/** @type {any} */ line) => line.key === this.main?.key) ||
      lines.find((/** @type {any} */ line) => String(line.variant_id) === String(this.main?.variant) && !line.parent_relationship) ||
      null
    );
  }

  /**
   * Drawer: adds the add-ons tagged under the cart line in one request (`parent_line_key`, the line's key from Liquid);
   * in Whole set the line is tagged first (one cart change, unless it has the tag, as Kaching tagged it)
   * @param {BundleItem[]} items
   * @param {HTMLButtonElement} button
   * @param {(text: string) => void} message
   * @returns {Promise<boolean>}
   */
  async #addToLine(items, button, message) {
    if (!items.length || !this.main?.key) return false;
    const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
    const cartUrl = `${routes().cart_url || '/cart'}.js`;
    const handle = String(this.data.offer.handle);
    const readCart = () => fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((response) => response.json());
    /** @param {string} parent */
    const add = (parent) =>
      fetch(routes().cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: json,
        body: JSON.stringify({
          items: items.map((item) => ({
            id: Number(/** @type {any} */ (item.selectedVariant()).id),
            quantity: 1,
            parent_line_key: parent,
            properties: { [TAG]: handle },
          })),
          sections: cartSections().join(','),
          sections_url: window.location.pathname,
        }),
      });
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    message('');
    let changed = false;
    try {
      let key = String(this.main.key);
      if (this.set) {
        // The line's properties as the cart has them (Liquid's copy is HTML-escaped), kept with the tag added
        const main = this.#findMain((await readCart()).items || []);
        if (!main) throw new Error('main line');
        key = main.key;
        if (main.properties?.[TAG] !== handle) {
          const response = await fetch(`${routes().cart_change_url || '/cart/change'}.js`, {
            method: 'POST',
            headers: json,
            body: JSON.stringify({ id: main.key, quantity: main.quantity, properties: { ...(main.properties || {}), [TAG]: handle } }),
          });
          const cart = await response.json();
          if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'change');
          changed = true;
          const tagged = (cart.items || []).find(
            (/** @type {any} */ line) =>
              String(line.variant_id) === String(main.variant_id) && line.properties?.[TAG] === handle && !line.parent_relationship
          );
          if (!tagged) throw new Error('main line');
          key = tagged.key;
        }
      }

      let response = await add(key);
      let reply = await response.json();
      // The drawer was older than the cart (the line changed elsewhere): find the line again and try once more
      if ((!response.ok || reply.status) && !this.set) {
        const main = this.#findMain((await readCart()).items || []);
        if (main && main.key !== key) {
          response = await add(main.key);
          reply = await response.json();
        }
      }
      if (!response.ok || reply.status) throw new Error(reply.description || reply.message || 'add');
      for (const added of reply.items || []) {
        if (!(added.final_price < added.original_price)) {
          console.warn('[okt-bundle-offer] No bundle discount on the line yet', handle, added.original_price, added.final_price);
        }
      }
      await this.#announce('cart', reply.sections, items, false, (reply.items || [])[0]?.key);
      return true;
    } catch (error) {
      message(this.context.t.error);
      // The main line changed: the drawer re-renders, so the block holds its new key
      if (changed) await this.#announce('cart', undefined, items, true);
      console.warn('[okt-bundle-offer]', error);
      return false;
    } finally {
      // The drawer's refresh takes the added add-on's line away; a button that stays is ready again
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  /**
   * Tells Horizon's cart (drawer, count) about the change, with the reply's sections when there are some
   * @param {'product' | 'cart'} context
   * @param {Record<string, string> | undefined} sections
   * @param {BundleItem[]} items
   * @param {boolean} didError
   * @param {string} [addedKey]
   * @returns {Promise<any>} The cart
   */
  async #announce(context, sections, items, didError, addedKey) {
    const cart = await fetch(`${routes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((response) =>
      response.json()
    );
    const deferred = CartLinesUpdateEvent.createPromise();
    const drawer = this.closest('dialog');
    const lines = items.map((item) => ({ merchandiseId: String(item.selectedVariant()?.id || ''), quantity: 1 }));
    if (context === 'product' && this.main) lines.push({ merchandiseId: String(this.main.variant), quantity: this.#mainQuantity() });
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context,
        lines,
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections, itemCount: cart.item_count, source: 'okt-bundle-offer', didError },
    });

    // In the drawer the block goes or changes with the re-render; the focus moves to the new line
    if (addedKey && drawer) {
      await new Promise((resolve) => setTimeout(resolve, 300));
      const row = drawer.querySelector(`[data-key="${CSS.escape(addedKey)}"]`);
      const target = /** @type {HTMLElement | null} */ (
        row?.querySelector('a[href], button') || drawer.querySelector('#cart-drawer-heading')
      );
      if (target) {
        if (!target.matches('a[href], button')) target.setAttribute('tabindex', '-1');
        target.focus();
      }
    }
    return cart;
  }

  /**
   * Quick view (D-142 BU5): Horizon's quick add window with the add-on's page; its button picks the add-on for the
   * bundle on a product page, or adds it under the line in the drawer, so the line is tagged
   * @param {BundleItem} item
   * @param {HTMLElement} opener
   */
  async #openQuickView(item, opener) {
    const dialog = /** @type {any} */ (document.getElementById('quick-add-dialog'));
    const url = item.url;
    if (!url) return;
    if (!dialog) {
      window.location.href = url;
      return;
    }
    try {
      await defineQuickView();
    } catch {
      window.location.href = url;
      return;
    }
    // Outside the block, so a drawer refresh doesn't take it away
    if (!this.#quickView?.isConnected) {
      this.#quickView = document.createElement('okt-bundle-quick-view');
      this.#quickView.hidden = true;
      document.body.append(this.#quickView);
    }
    const view = /** @type {any} */ (this.#quickView);
    view.dataset.productUrl = url;
    view.dataset.productTitle = String(item.data.title || '');
    await view.handleClick(new Event('click', { cancelable: true }));
    this.#takeOver(dialog, item, opener);
  }

  /**
   * While the window shows the add-on: its button reads "Choose for the bundle" (or "Add" in the drawer) and does
   * that; the wallet buttons are hidden (CSS). Caught on the window's capture phase, before Horizon's handlers.
   * @param {any} dialog
   * @param {BundleItem} item
   * @param {HTMLElement} opener
   */
  #takeOver(dialog, item, opener) {
    const content = document.getElementById('quick-add-modal-content');
    if (!content) return;
    dialog.dataset.oktBundleView = '';
    const label = this.mode === 'cart' ? this.context.t.add : this.context.t.chooseFor;

    const relabel = () => {
      const button = content.querySelector('button[name="add"]');
      if (!button) return;
      const text = Array.from(button.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.nodeValue?.trim());
      if (text && text.nodeValue?.trim() !== label) text.nodeValue = label;
      else if (!text) button.prepend(document.createTextNode(label));
    };
    const observer = new MutationObserver(relabel);
    relabel();
    observer.observe(content, { childList: true, subtree: true, characterData: true });

    const onClick = (/** @type {Event} */ event) => {
      const target = /** @type {Element} */ (event.target);
      const add = target instanceof Element ? target.closest('button[name="add"]') : null;
      if (!add || !content.contains(add)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const form = add.closest('form');
      const id = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]') ?? null)?.value || '';
      const quantity = parseInt(/** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]') ?? null)?.value || '1', 10) || 1;
      this.#chooseFromView(dialog, item, id, quantity);
    };
    const onSubmit = (/** @type {Event} */ event) => {
      if (event.target instanceof Node && content.contains(event.target)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener('click', onClick, { capture: true });
    window.addEventListener('submit', onSubmit, { capture: true });
    const end = () => {
      observer.disconnect();
      window.removeEventListener('click', onClick, { capture: true });
      window.removeEventListener('submit', onSubmit, { capture: true });
      delete dialog.dataset.oktBundleView;
      if (opener.isConnected) opener.focus({ preventScroll: true });
    };
    dialog.addEventListener('dialog:close', end, { once: true });
  }

  /**
   * The window's button: the add-on in that size, ticked; in the drawer, added under its line
   * @param {any} dialog
   * @param {BundleItem} item
   * @param {string} id - The variant's numeric id
   * @param {number} quantity
   */
  async #chooseFromView(dialog, item, id, quantity) {
    const variant = item.variants.find((entry) => String(entry.id) === id && entry.available);
    if (!variant) return;
    if (item.varying.length) {
      item.setSize(id);
      item.picked = true;
      item.open = true;
    }
    if (this.mode === 'cart') {
      item.update();
      // The window closes either way; an error shows under the add-on's line
      dialog.closeDialog?.();
      const button = /** @type {HTMLButtonElement | null} */ (item.part('.okt-bundle-add__button'));
      await this.#addToLine([item], button || document.createElement('button'), (text) => item.message(text));
      return;
    }
    item.checked = true;
    item.chosen = true;
    const input = /** @type {HTMLInputElement | null} */ (item.part('.okt-bundle-quantity input'));
    if (input) input.value = String(Math.max(1, quantity));
    if (this.compact && !this.open) this.#toggle(true);
    item.update();
    this.#refreshTotals();
    dialog.closeDialog?.();
  }

  /**
   * A drawer refresh: a new line or offer sets the block up again, as do parts the refresh drew anew (still loading)
   * @param {MutationRecord[]} records
   */
  #onMutation(records) {
    if (this.#filling || !records.length) return;
    const identity = (this.dataset.oktBundle || '') !== this.#identity;
    const shown = (this.#hidden && !this.hidden) || this.items.some((item) => !item.available && !item.element.hidden);
    if (!identity && !shown && !this.querySelector('[data-state="loading"]')) return;
    if (identity) this.items = [];
    this.#render();
  }
}

if (!customElements.get('okt-bundle-offer')) customElements.define('okt-bundle-offer', OktBundleOffer);
